import { create } from 'zustand'
import { teamApi, TeamMember, Invite, InviteEligibility, TeamSeatStatus } from '@/lib/api'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'

interface TeamStore {
  members: TeamMember[]
  invites: Invite[]
  // Whether the account may create a NEW invite right now (SMB/Enterprise
  // plan gate) and which plan that was decided against. null before the
  // first fetch resolves, or if the fetch fails/is refused (Rep etc. never
  // call this, see TeamSection), treated as "not eligible" for rendering,
  // never surfaced as an error toast since a 403 here is expected for most
  // roles/plans, not a failure.
  inviteEligibility: InviteEligibility | null
  // Paid vs occupied seats (Momentum per-seat spec §5). null before the first
  // fetch or when refused (roles below Owner/Admin never ask); the Team page
  // treats null as "unknown, don't gate client-side" and lets the server
  // answer 402/403 on the actual invite.
  seatStatus: TeamSeatStatus | null
  loading: boolean
  error: string | null

  fetchMembers: () => Promise<void>
  fetchInvites: () => Promise<void>
  fetchInviteEligibility: () => Promise<void>
  fetchSeatStatus: () => Promise<void>
  createInvite: (data: { email: string; role: string }) => Promise<Invite>
  revokeInvite: (inviteId: number) => Promise<void>
  updateMemberRole: (userId: number, role: string) => Promise<void>
  removeMember: (userId: number) => Promise<void>
  transferOwnership: (userId: number) => Promise<void>
  reset: () => void
}

const initialState = {
  members: [],
  invites: [],
  inviteEligibility: null,
  seatStatus: null,
  loading: false,
  error: null,
}

export const useTeamStore = create<TeamStore>((set, get) => ({
  ...initialState,

  fetchMembers: async () => {
    set({ loading: true, error: null })
    try {
      const response = await teamApi.listMembers()
      set({ members: response.data, loading: false })
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to load team members', loading: false })
      throw error
    }
  },

  fetchInvites: async () => {
    set({ loading: true, error: null })
    try {
      const response = await teamApi.listInvites()
      set({ invites: response.data, loading: false })
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to load invites', loading: false })
      throw error
    }
  },

  fetchInviteEligibility: async () => {
    try {
      const response = await teamApi.inviteEligibility()
      set({ inviteEligibility: response.data })
    } catch {
      // Expected for any role below Owner/Admin (403) and while the account
      // has no active plan, not an error to surface, just "can't invite."
      set({ inviteEligibility: null })
    }
  },

  fetchSeatStatus: async () => {
    try {
      const response = await teamApi.seatStatus()
      set({ seatStatus: response.data })
    } catch {
      set({ seatStatus: null })
    }
  },

  createInvite: async (data) => {
    set({ loading: true, error: null })
    try {
      const response = await teamApi.createInvite(data)
      set((state) => ({ invites: [response.data, ...state.invites], loading: false }))
      toast.success(`Invite sent to ${data.email}`)
      // The invite just took a seat; refresh so the summary and the next
      // "Add member" decision see it (best effort, never fails the invite).
      get().fetchSeatStatus().catch(() => {})
      return response.data
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to send invite'
      set({ error: message, loading: false })
      // 402 (all paid seats in use) is handled by the Team page, which opens
      // the buy-more-seats modal instead of showing an error.
      if (error?.response?.status !== 402) toast.error(message)
      throw error
    }
  },

  revokeInvite: async (inviteId) => {
    try {
      await teamApi.revokeInvite(inviteId)
      set((state) => ({ invites: state.invites.filter((i) => i.id !== inviteId) }))
      toast.success('Invite revoked')
      get().fetchSeatStatus().catch(() => {})
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invite')
      throw error
    }
  },

  updateMemberRole: async (userId, role) => {
    try {
      const response = await teamApi.updateMemberRole(userId, role)
      set((state) => ({
        members: state.members.map((m) => (m.id === userId ? response.data : m)),
      }))
      toast.success('Role updated')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update role')
      throw error
    }
  },

  removeMember: async (userId) => {
    try {
      await teamApi.removeMember(userId)
      set((state) => ({ members: state.members.filter((m) => m.id !== userId) }))
      toast.success('Team member removed')
      get().fetchSeatStatus().catch(() => {})
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member')
      throw error
    }
  },

  // AB#22363. This is the one team action that changes the CALLER's own role
  // (owner -> admin), so the permission map the whole UI reads is stale the
  // moment it succeeds: refetch it, and refetch the roster, rather than
  // patching one row optimistically.
  transferOwnership: async (userId) => {
    try {
      const response = await teamApi.transferOwnership(userId)
      toast.success(`Ownership transferred to ${response.data.new_owner_email}`)
      await useAuthStore.getState().fetchPermissions()
      await get().fetchMembers()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to transfer ownership')
      throw error
    }
  },

  reset: () => set(initialState),
}))
